import {
  Clear as ClearIcon,
  Download as DownloadIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { OrderStatusFilters } from "../../../types/admin";

interface OrderStatusFiltersPanelProps {
  filters: OrderStatusFilters;
  onFiltersChange: (filters: OrderStatusFilters) => void;
  onExport?: () => void;
  loading?: boolean;
}

const OrderStatusFiltersPanel: React.FC<OrderStatusFiltersPanelProps> = ({
  filters,
  onFiltersChange,
  onExport,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState<OrderStatusFilters>(filters);

  const handleFilterChange = (key: keyof OrderStatusFilters, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: OrderStatusFilters = {
      pageNumber: 1,
      pageSize: 20,
      sortBy: "sortOrder",
      sortDescending: false,
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const handleSearch = () => {
    onFiltersChange(localFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (localFilters.search) count++;
    if (localFilters.isActive !== undefined) count++;
    if (localFilters.sortBy && localFilters.sortBy !== "sortOrder") count++;
    if (localFilters.sortDescending) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Paper sx={{ mb: 3, p: 2 }}>
      {/* Main Search Bar */}
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <TextField
          fullWidth
          placeholder={t("admin.orderStatus.searchPlaceholder")}
          value={localFilters.search || ""}
          onChange={(e) =>
            setLocalFilters({ ...localFilters, search: e.target.value })
          }
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
            ),
            endAdornment: localFilters.search && (
              <IconButton
                size="small"
                onClick={() => handleFilterChange("search", "")}
                disabled={loading}
              >
                <ClearIcon />
              </IconButton>
            ),
          }}
          disabled={loading}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading}
          startIcon={<SearchIcon />}
        >
          {t("admin.orderStatus.search")}
        </Button>
        <Button
          variant="outlined"
          onClick={() => setExpanded(!expanded)}
          startIcon={<FilterListIcon />}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          disabled={loading}
        >
          {t("admin.orderStatus.filters")}
          {activeFiltersCount > 0 && (
            <Chip
              label={activeFiltersCount}
              size="small"
              color="primary"
              sx={{ ml: 1, height: 20, minWidth: 20 }}
            />
          )}
        </Button>
        {onExport && (
          <Button
            variant="outlined"
            onClick={onExport}
            disabled={loading}
            startIcon={<DownloadIcon />}
          >
            {t("admin.orderStatus.export")}
          </Button>
        )}
      </Box>

      {/* Advanced Filters */}
      <Collapse in={expanded}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t("admin.orderStatus.status")}</InputLabel>
              <Select
                value={localFilters.isActive ?? ""}
                onChange={(e) =>
                  handleFilterChange(
                    "isActive",
                    e.target.value === ""
                      ? undefined
                      : e.target.value === "true",
                  )
                }
                label={t("admin.orderStatus.status")}
                disabled={loading}
              >
                <MenuItem value="">{t("admin.orderStatus.allStatuses")}</MenuItem>
                <MenuItem value="true">{t("admin.orderStatus.activeOnly")}</MenuItem>
                <MenuItem value="false">{t("admin.orderStatus.inactiveOnly")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t("admin.orderStatus.sortBy")}</InputLabel>
              <Select
                value={localFilters.sortBy || "sortOrder"}
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                label={t("admin.orderStatus.sortBy")}
                disabled={loading}
              >
                <MenuItem value="sortOrder">{t("admin.orderStatus.sortOrder")}</MenuItem>
                <MenuItem value="name">{t("admin.orderStatus.name")}</MenuItem>
                <MenuItem value="displayName">{t("admin.orderStatus.displayName")}</MenuItem>
                <MenuItem value="createdAt">{t("admin.orderStatus.createdDate")}</MenuItem>
                <MenuItem value="updatedAt">{t("admin.orderStatus.updatedDate")}</MenuItem>
                <MenuItem value="orderCount">{t("admin.orderStatus.usageCount")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t("admin.orderStatus.sortDirection")}</InputLabel>
              <Select
                value={localFilters.sortDescending ? "desc" : "asc"}
                onChange={(e) =>
                  handleFilterChange(
                    "sortDescending",
                    e.target.value === "desc",
                  )
                }
                label={t("admin.orderStatus.sortDirection")}
                disabled={loading}
              >
                <MenuItem value="asc">{t("admin.orderStatus.ascending")}</MenuItem>
                <MenuItem value="desc">{t("admin.orderStatus.descending")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" alignItems="center" gap={1} height="100%">
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading || activeFiltersCount === 0}
                startIcon={<ClearIcon />}
                fullWidth
              >
                {t("admin.orderStatus.clearFilters")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Collapse>

      {/* Active Filters Display */}
      {activeFiltersCount > 0 && (
        <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
          {localFilters.search && (
            <Chip
              label={t("admin.orderStatus.searchChip", { term: localFilters.search })}
              onDelete={() => handleFilterChange("search", "")}
              color="primary"
              variant="outlined"
            />
          )}
          {localFilters.isActive !== undefined && (
            <Chip
              label={t("admin.orderStatus.statusChip", { status: localFilters.isActive ? t("admin.orderStatus.active") : t("admin.orderStatus.inactive") })}
              onDelete={() => handleFilterChange("isActive", undefined)}
              color="primary"
              variant="outlined"
            />
          )}
          {localFilters.sortBy && localFilters.sortBy !== "sortOrder" && (
            <Chip
              label={t("admin.orderStatus.sortChip", { field: localFilters.sortBy })}
              onDelete={() => handleFilterChange("sortBy", "sortOrder")}
              color="primary"
              variant="outlined"
            />
          )}
          {localFilters.sortDescending && (
            <Chip
              label={t("admin.orderStatus.descending")}
              onDelete={() => handleFilterChange("sortDescending", false)}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

export default OrderStatusFiltersPanel;
